import { IGroup } from "@app/main/components/resourceManagement/groups/_models/IGroup";
import { IAutocompleteMatch } from "@directives/_forms/autocomplete/autocomplete.component";
import { GeofenceType } from "@enums/GeofenceType";
import { IGeofence } from "@interfaces/IGeofences";
import { GeofenceService } from "@services/geofence.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { ITmsRouteRules } from "@tms/_models/ITmsRouteRules";
import { PlannedRouteVisitService } from "@tms/_services/planned-route-visit.service";
import { IPlannedRouteVisitListValidation, PlannedRouteVisitListValidationReason, SchedulingService } from "@tms/_services/scheduling.service";
import * as angular from "angular";
import { copy } from "angular";
import "./scheduleVisitsList.component.scss";

export interface IAvailableGoogleRoute {
    index: number;
    label: string;
    cost: string;
    distance: string;
}

export interface IRouteVisitsUpdate {
    visits: IPlannedRouteVisit[];
    fitMap: boolean;
}

interface IPredefinedOnsiteTime {
    minutes: number;
    label: string;
}

interface IPredefinedOnsiteTimeMap {
    [minutes: string]: IPredefinedOnsiteTime;
}

enum PlannedRouteVisitWarningType {
    OutOfSync,
    Validation
}

interface IComponentBindings {
    visits?: IPlannedRouteVisit[];
    routeRules?: ITmsRouteRules;
    startDepot?: IGroup;
    endDepot?: IGroup;
    startMoment: moment.Moment;
    forceVisitsUpdate?: number;
    onChanged: (args: { update: IRouteVisitsUpdate }) => void;
    onReturnToStart: () => void;
    onCalculatingRoute: (args: { calculating: boolean }) => void;
    onStartMomentChanged: (args: { startMoment: moment.Moment, }) => void;
}

export class ScheduleVisitsListComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    visits: IPlannedRouteVisit[];
    routeRules: ITmsRouteRules;
    startMoment: moment.Moment;
    forceVisitsUpdate: number;
    onChanged: (args: { update: IRouteVisitsUpdate, }) => void;
    onReturnToStart: () => void;
    onCalculatingRoute: (args: { calculating: boolean }) => void;
    onStartMomentChanged: (args: { startMoment: moment.Moment, }) => void;

    // google response
    routes: google.maps.DirectionsRoute[];
    selectedRoute: google.maps.DirectionsRoute;

    // raw data
    geofences: IGeofence[];

    // spinners
    calculatingRoute: boolean;

    //
    ready: boolean;
    validation: IPlannedRouteVisitListValidation;
    displayVisits: IPlannedRouteVisit[];
    startHour: number;
    startMinute: number;
    returnToStart: boolean;

    showingRecalculateWarning: boolean;
    maxVisitsForAutomatedRecalculate = 10;
    routeWarning: PlannedRouteVisitWarningType;
    routeWarningDebounceFn: any;
    routeCalculationId: number = -1;

    PlannedRouteVisitWarningType = PlannedRouteVisitWarningType;

    onSiteTimes: IPredefinedOnsiteTimeMap = {
        [0]: { minutes: 0, label: "0 mins" },
        [5]: { minutes: 5, label: "5 mins" },
        [10]: { minutes: 10, label: "10 mins" },
        [15]: { minutes: 15, label: "15 mins" },
        [30]: { minutes: 30, label: "30 mins" },
        [45]: { minutes: 45, label: "45 mins" },
        [60]: { minutes: 60, label: "60 mins" }
    };

    // debugging
    showDebugLabels: boolean = false;

    static $inject = ["$scope", "$element", "$timeout", "translationService", "schedulingService", "plannedRouteVisitService", "geofenceService", "veriDate", "$mdDialog"];

    constructor(

        private readonly $scope: angular.IScope,
        private readonly $element: JQuery,
        private readonly $timeout: angular.ITimeoutService,
        private readonly translationService: TranslationService,
        private readonly schedulingService: SchedulingService,
        private readonly plannedRouteVisitService: PlannedRouteVisitService,

        private readonly geofenceService: GeofenceService,
        private readonly veriDate: VeriDate,

        private readonly $mdDialog: angular.material.IDialogService
    ) {
    }

    $onInit() {
        this.geofenceService.getGeofences().then((geofences: IGeofence[]) => {
            this.geofences = geofences;
        }).finally(() => {
            this.ready = true;

            this.refreshReturnToStartState();
            this.calculateRoute();
            this.refreshDisplayVisits();
            this.validate();
        });
    }

    $onChanges(changes: angular.IOnChangesObject) {
        const visitsChange = changes["visits"];
        const routeRulesChange = changes["routeRules"];
        const startMomentChange = changes["startMoment"];
        const forceVisitsUpdateChange = changes["forceVisitsUpdate"];

        if (visitsChange && this.ready) {
            this.filterCancelledVisits();
            this.calculateRoute();
        }

        if (routeRulesChange) {
            this.processRouteRulesChange(routeRulesChange);
        }

        if (startMomentChange && !startMomentChange.isFirstChange()) {
            this.setFirstVisitDate(this.startMoment.toDate());
            this.refreshDisplay();
        }

        if (forceVisitsUpdateChange && !forceVisitsUpdateChange.isFirstChange()) {
            this.refreshReturnToStartState();
            this.calculateRoute();
        }
    }

    emitVisits(fitMap: boolean = false): void {
        this.$scope.$applyAsync();

        if (!this.onChanged) {
            return;
        }

        this.onChanged({
            update: {
                visits: this.visits,
                fitMap
            }
        });
    }

    promptRecalculateTimings(): void {
        const titleTranslation = this.translationService.t("ScheduleVisitsList.CalculateTitle");
        const contentTranslation = this.translationService.t("ScheduleVisitsList.CalculateDescription");
        const yesTranslation = this.translationService.t("Common.Yes");
        const cancelTranslation = this.translationService.t("Common.Cancel");

        const deleteConfirm = this.$mdDialog
            .confirm()
            .title(titleTranslation)
            .textContent(contentTranslation)
            .ok(yesTranslation)
            .cancel(cancelTranslation);

        this.$mdDialog
            .show(deleteConfirm)
            .then(() => {
                this.calculateVisitingTimes();
            });
    }

    returnToStartChanged(): void {
        if (this.returnToStart) {
            this.setReturnToStartLocation();
            return;
        }

        this.onEndingLocationMatchCleared();
    }

    onVisitOnSiteTimeChanged(visit: IPlannedRouteVisit): void {
        visit.onSiteTimeMins = +visit.onSiteTimeMins;
        visit.OnSiteTime = this.veriDate.convertMinsToTimeSpan(visit.onSiteTimeMins, true);

        this.schedulingService.setVisitAsDirty(visit);

        this.refreshDisplay();
    }

    onStartingLocationMatch(match: IAutocompleteMatch): void {
        this.updateVisitWithLocationMatch(this.visits[0], match);
        this.setReturnToStartLocation();
    }

    onStartingLocationMatchCleared(): void {
        this.resetVisitData(this.visits[0]);
        this.refreshReturnToStartState();
        this.validate();
        this.calculateRoute();
    }

    onEndingLocationMatch(match: IAutocompleteMatch): void {
        this.updateVisitWithLocationMatch(this.visits[this.visits.length - 1], match);
        this.refreshReturnToStartState();
    }

    onEndingLocationMatchCleared(): void {
        this.resetVisitData(this.visits[this.visits.length - 1]);
        this.setReturnToStart(false);
        this.validate();
        this.calculateRoute();
    }

    onVisitLocationMatched(visit: IPlannedRouteVisit, match: IAutocompleteMatch): void {
        this.resetVisitData(visit);

        if (!match) {
            console.log(`No match for visit`, visit);
            return;
        }

        this.updateVisitWithLocationMatch(visit, match);
    }

    onVisitLocationMatchCleared(visit: IPlannedRouteVisit): void {
        this.resetVisitData(visit);
        this.validate();
        this.calculateRoute();
    }

    onStartDepartureTimeChanged(newTime: string): void {
        this.updateStartMomentUsingTimespan(newTime);

        this.visits[0].PlannedArrivalTime = this.startMoment.toDate();
        this.visits[0].plannedArrivalTimeSpan = newTime;

        this.visits[0].PlannedDepartureTime = this.startMoment.toDate();
        this.visits[0].plannedDepartureTimeSpan = newTime;

        this.schedulingService.setVisitAsDirty(this.visits[0]);

        this.refreshDisplay();
    }

    onEndArrivalTimeChanged(newTime: string): void {
        const endLocation = this.visits[this.visits.length - 1];

        this.changeVisitArrivalTimeFromTimestamp(endLocation, newTime);
    }

    onVisitArrivalTimeChanged(visit: IPlannedRouteVisit, newTime: string): void {
        this.changeVisitArrivalTimeFromTimestamp(visit, newTime);

        if (visit === this.visits[0]) {
            this.updateStartMomentUsingTimespan(newTime);
        }
    }

    addNewVisit(isStartLocation: boolean = false, isEndLocation: boolean = false, fromTemplate: boolean = false): void {
        const newVisit = this.schedulingService.createPlannedVisitTemplate();

        const startLocationRequired: boolean = this.routeRules?.RouteMatchingRules?.StartRouteOnDepartStartingLocation;
        const endLocationRequired: boolean = this.routeRules?.RouteMatchingRules?.EndRouteOnArriveAtEndingLocation;
        const isBasicVisit = !isStartLocation && !isEndLocation;

        // create start location
        if (isStartLocation) {
            this.visits.splice(0, 0, newVisit);
        }

        // create end location
        else if (isEndLocation) {
            this.visits.push(newVisit);
        }

        // new visit at end, avoids end location if present
        else if (endLocationRequired && isBasicVisit) {
            this.visits.splice(this.visits.length - 1, 0, newVisit);

            this.visits[this.visits.length - 1].travelTimeMins = 0;
            this.visits[this.visits.length - 1].TravelDistanceMetres = 0;
        }

        // new visit at end
        else if (isBasicVisit) {
            this.visits.push(newVisit);
        }

        if (fromTemplate) {
            this.schedulingService.setVisitsAsDirty(this.visits);
            this.schedulingService.setVisitAsNotDirty(newVisit);
        }

        this.$timeout(() => {
            this.scrollToBottomOfVisits();
        }, 10);

        this.validate();
        this.refreshDisplayVisits();
    }

    onVisitRemoved(visit: IPlannedRouteVisit): void {
        if (!visit) {
            return;
        }

        const realIndex = this.visits.indexOf(visit);

        this.visits.splice(realIndex, 1);

        this.refreshDisplayVisits();
        this.calculateRoute();
    }

    moveVisitUp(index: number): void {
        const realIndex = this.getDisplayVisitIndexToRealIndex(index);

        this.visits.splice(realIndex, 0, this.visits.splice((realIndex - 1), 1)[0]);

        this.refreshDisplayVisits();
        this.calculateRoute();
    }

    moveVisitDown(index: number): void {
        const realIndex = this.getDisplayVisitIndexToRealIndex(index);

        this.visits.splice(realIndex, 0, this.visits.splice((realIndex - 1), 1)[0]);

        this.refreshDisplayVisits();
        this.calculateRoute();
    }

    moveVisitToIndex(visit: IPlannedRouteVisit, targetIndex: number): void {
        const displayVisit = this.displayVisits.find(displayVisit => displayVisit.Id === visit.Id);
        const indexOfDisplayVisit = this.displayVisits.indexOf(displayVisit);
        const indexOfVisit = this.getDisplayVisitIndexToRealIndex(indexOfDisplayVisit);
        const newTargetIndex = this.getDisplayVisitIndexToRealIndex(targetIndex);

        this.visits.splice(newTargetIndex, 0, this.visits.splice(indexOfVisit, 1)[0]);

        this.calculateRoute();
    }

    recalculateRoute(): void {
        this.calculateRoute();
    }

    // ---

    private resetVisitData(visit: IPlannedRouteVisit): void {
        visit.Name = null;
        visit.RouteStopLocation = null;
        visit.RouteStopLocationJson = null;
        visit.GeofenceId = null;
        visit.Geofence = null;

        this.resetVisitTravel(visit);

        this.schedulingService.setVisitAsDirty(visit);
    }

    private resetVisitTravel(visit: IPlannedRouteVisit): void {
        visit.TravelDistanceMetres = null;
        visit.LinePoints = null;
        visit.TravelTime = null;
        visit.travelTimeMins = null;
    }

    private filterCancelledVisits(): void {
        this.visits = this.visits.filter(visit => visit.Status !== RouteVisitStatus.Cancelled);
    }

    private showRecalculateWarning(firstRun: boolean = false): void {
        if (firstRun) {
            return;
        }

        this.showingRecalculateWarning = true;
    }

    private clearRecalculateWarning(): void {
        this.showingRecalculateWarning = false;
    }

    private refreshRouteWarning(): void {
        this.routeWarning = null;

        if (!this.validation) {
            return;
        }

        if (this.showingRecalculateWarning) {
            if (this.validation.reasonMap[0] || this.validation.reasonMap[1] || this.validation.reasonMap[4]) {
                return;
            }

            this.routeWarning = PlannedRouteVisitWarningType.OutOfSync;

        } else {
            if (!this.validation.valid
                && !this.validation.reasonMap[PlannedRouteVisitListValidationReason.NoVisits]
                && !this.validation.reasonMap[PlannedRouteVisitListValidationReason.VisitMissingLocation]
                && !this.validation.reasonMap[PlannedRouteVisitListValidationReason.VisitSameLocationAsPrevious]) {
                this.routeWarning = PlannedRouteVisitWarningType.Validation;

            }
        }
    }

    private calculateRoute(): void {
        const currentRouteCalculationId = ++this.routeCalculationId;

        this.setCalculatingRoute(true);
        this.clearRecalculateWarning();
        this.clearCurrentCalculationsOnVisits(this.visits);

        const totalValidVisits = this.visits.filter(visit => !!visit.RouteStopLocation)?.length;

        if (totalValidVisits < 2) {
            this.refreshDisplay();
            this.setCalculatingRoute(false);
            return;
        }

        this.plannedRouteVisitService.updateDirectionsForVisits(this.visits, true, true, false).then((updatedVisits: IPlannedRouteVisit[]) => {
            if (this.routeCalculationId > currentRouteCalculationId) {
                return;
            }

            this.refreshDisplay(true);

        }).finally(() => {
            if (this.routeCalculationId > currentRouteCalculationId) {
                return;
            }

            this.setCalculatingRoute(false);
        });
    }

    private setCalculatingRoute(value: boolean): void {
        this.calculatingRoute = value;

        if (this.onCalculatingRoute) {
            this.onCalculatingRoute({ calculating: value });
        }
    }

    private clearCurrentCalculationsOnVisits(visits: IPlannedRouteVisit[]): void {
        visits?.forEach(visit => this.resetVisitTravel(visit));
    }

    private refreshDisplay(fitMap: boolean = false): void {
        this.refreshDisplayVisits();

        this.emitVisits(fitMap);
    }

    private updateStartMomentUsingTimespan(timeSpan: string): void {
        if (!timeSpan) {
            return;
        }

        const hour = Number(timeSpan.substring(0, timeSpan.indexOf(":")));
        const minute = Number(timeSpan.substring(timeSpan.indexOf(":") + 1, timeSpan.length));

        if (!hour || !minute) {
            return;
        }

        const newStartMoment = this.veriDate.createMoment(this.startMoment).clone();
        newStartMoment.set("hour", +hour);
        newStartMoment.set("minute", +minute);

        this.startMoment = newStartMoment;

        if (this.onStartMomentChanged) {
            this.onStartMomentChanged({ startMoment: this.startMoment });
        }
    }

    private calculateVisitingTimes(): void {
        if (!this.visits?.length) {
            return;
        }

        let totalRouteTimeMinutes = 0;

        const startDate = this.startMoment.toDate();

        this.visits.forEach((visit, index) => {
            this.plannedRouteVisitService.updateTimeProperties(visit); // preparing properties for calculations

            // handle first visit/location
            if (index === 0) {
                if (!visit.PlannedArrivalTime) {
                    visit.PlannedArrivalTime = startDate;
                }

                if (this.routeRules?.RouteMatchingRules?.StartRouteOnDepartStartingLocation) {
                    visit.PlannedDepartureTime = startDate;
                    visit.OnSiteTime = "00:00:00";

                } else {
                    visit.PlannedDepartureTime = this.veriDate.createMoment(new Date(visit.PlannedArrivalTime)).clone().add(visit.onSiteTimeMins, "m").toDate();
                }

                this.plannedRouteVisitService.updateTimeProperties(visit);
                return;
            }

            const previousVisit = this.visits[index - 1];
            const duration = new Date(visit.PlannedArrivalTime).getTime() - new Date(previousVisit.PlannedDepartureTime).getTime();
            const minutesToThisVisit = Math.abs(duration / 1000 / 60);


            // define arrival / departure times based on google travel time
            visit.PlannedArrivalTime = this.veriDate.createMoment(previousVisit.PlannedDepartureTime).clone().add(visit.travelTimeMins, "m").toDate();
            visit.PlannedDepartureTime = this.veriDate.createMoment(visit.PlannedArrivalTime).clone().add(visit.onSiteTimeMins, "m").toDate();

            totalRouteTimeMinutes += visit.travelTimeMins;
            totalRouteTimeMinutes += visit.onSiteTimeMins;

            this.plannedRouteVisitService.updateTimeProperties(visit);
            this.plannedRouteVisitService.updateOnSiteTimeMins(visit);
        });

        this.validate();
        this.emitVisits();
    }

    private refreshDisplayVisits(): void {
        this.displayVisits = [];

        this.displayVisits = (this.routeRules.RouteMatchingRules.StartRouteOnDepartStartingLocation)
            ? this.visits.slice(1)
            : this.visits.slice()

        if (this.routeRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation) {
            this.displayVisits = this.displayVisits.slice(0, -1);
        }
    }

    private getDisplayVisitIndexToRealIndex(displayIndex: number): number {
        const requireStartLocation: boolean = this.routeRules?.RouteMatchingRules?.StartRouteOnDepartStartingLocation;
        const visitsIndex = (requireStartLocation) ? displayIndex + 1 : displayIndex;

        return visitsIndex;
    }


    private scrollToBottomOfVisits(): void {
        const visitsContainer = this.$element.find(".visits-container")[0];

        if (!visitsContainer) {
            return;
        }

        visitsContainer.scrollTop = visitsContainer.scrollHeight;
    }

    private processRouteRulesChange(routeRulesChange: angular.IChangesObject<ITmsRouteRules>): void {

        const firstChange = routeRulesChange.isFirstChange();

        if (firstChange) {
            return;
        }

        let currentlyNeedsStartLocation;
        let currentlyNeedsEndLocation;
        let previouslyNeededStartLocation;
        let previouslyNeededEndLocation;
        let changeMade: boolean;

        if (routeRulesChange.currentValue.RouteMatchingRules) {
            currentlyNeedsStartLocation = routeRulesChange.currentValue.RouteMatchingRules.StartRouteOnDepartStartingLocation;
            currentlyNeedsEndLocation = routeRulesChange.currentValue.RouteMatchingRules.EndRouteOnArriveAtEndingLocation;
        }

        if (routeRulesChange.previousValue.RouteMatchingRules) {
            previouslyNeededStartLocation = routeRulesChange.previousValue.RouteMatchingRules.StartRouteOnDepartStartingLocation;
            previouslyNeededEndLocation = routeRulesChange.previousValue.RouteMatchingRules.EndRouteOnArriveAtEndingLocation;
        }

        const addStartLocation = !previouslyNeededStartLocation && currentlyNeedsStartLocation;
        const addEndLocation = !previouslyNeededEndLocation && currentlyNeedsEndLocation;

        const removeStartLocation = previouslyNeededStartLocation && !currentlyNeedsStartLocation;
        const removeEndLocation = previouslyNeededEndLocation && !currentlyNeedsEndLocation;

        if (addStartLocation) {
            this.addNewVisit(true);
            changeMade = true;
        }

        if (addEndLocation) {
            this.addNewVisit(false, true);
            changeMade = true;
        }

        if (removeStartLocation) {
            this.visits.splice(0, 1);
            changeMade = true;
        }

        if (removeEndLocation) {
            this.visits.splice(this.visits.length - 1, 1);
            changeMade = true;
        }

        this.refreshReturnToStartState();

        if (changeMade) {
            this.emitVisits();
            this.calculateRoute();
        }
    }

    private setFirstVisitDate(newDate: Date): void {
        const firstVisit = this.visits[0];

        if (!firstVisit || !firstVisit.PlannedArrivalTime) {
            return;
        }

        const newMoment = this.veriDate.createMoment(newDate).clone();

        this.veriDate.setMomentToMinuteOfDay(newMoment, this.veriDate.getMinuteOfDayFromDate(new Date(firstVisit.PlannedArrivalTime)));

        const timeStamp = this.veriDate.convertMomentToTimespan(newMoment);

        firstVisit.PlannedArrivalTime = newMoment.toDate();
        firstVisit.plannedArrivalTimeSpan = timeStamp;

        firstVisit.PlannedDepartureTime = newMoment.toDate();
        firstVisit.plannedDepartureTimeSpan = timeStamp;
    }

    private validate(): void {
        this.validation = this.schedulingService.validateVisitList(this.visits);

        this.refreshRouteWarning();
    }

    private changeVisitArrivalTimeFromTimestamp(visit: IPlannedRouteVisit, newTime: string): void {
        const previousVisit = this.visits[this.visits.indexOf(visit) - 1];
        let relevantDate: Date = this.startMoment.toDate();

        if (previousVisit) {
            relevantDate = previousVisit.PlannedDepartureTime;
        }

        const previousVisitMinuteOfDay = this.veriDate.getMinuteOfDayFromDate(relevantDate);
        const currentVisitMinuteOfDay = this.veriDate.convertTimeSpanToMins(newTime);
        const newArrivalTimeMoment = this.veriDate.createMoment(relevantDate).clone();

        if (previousVisit && previousVisitMinuteOfDay > currentVisitMinuteOfDay) {
            newArrivalTimeMoment.add(1, "day");
        }

        this.veriDate.setMomentToMinuteOfDay(newArrivalTimeMoment, currentVisitMinuteOfDay);

        visit.PlannedArrivalTime = newArrivalTimeMoment.toDate();
        visit.plannedArrivalTimeSpan = newTime;

        this.updateVisitDepartureTime(visit);
        this.schedulingService.setVisitAsDirty(visit);

        this.refreshDisplay();
    }

    private updateVisitDepartureTime(visit: IPlannedRouteVisit): void {
        this.plannedRouteVisitService.updatePlannedArrivalTimeSpan(visit);
        this.plannedRouteVisitService.updateOnSiteTimeMins(visit);

        visit.PlannedDepartureTime = this.veriDate.createMoment(visit.PlannedArrivalTime).clone().add(visit.onSiteTimeMins, "m").toDate();

        this.plannedRouteVisitService.updatePlannedDepartureTimeSpan(visit);
    }

    private updateVisitWithLocationMatch(visit: IPlannedRouteVisit, match: IAutocompleteMatch): void {
        if (!visit || !match) {
            return;
        }

        this.schedulingService.setVisitAsDirty(visit);

        visit.Name = match.label;

        if (!visit.RouteStopLocation) {
            visit.RouteStopLocation = {}
        }

        if (match.source === "Geofence") {

            visit.GeofenceId = match.objectId;
            visit.Geofence = match.object;
            visit.RouteStopLocation = match.object;

        } else if (match.source === "Place" && match.location) {

            visit.RouteStopLocation.Latitude = match.location.lat;
            visit.RouteStopLocation.Longitude = match.location.lng;
            visit.RouteStopLocation.GeofenceType = GeofenceType.Circle;
            visit.RouteStopLocation.Radius = 250;
        }

        const indexOfVisit = this.visits.indexOf(visit);

        this.visits[indexOfVisit] = angular.copy(visit);
        this.visits[indexOfVisit].RouteStopLocationJson = JSON.stringify(this.visits[indexOfVisit].RouteStopLocation);

        this.validate();

        this.calculateRoute();
    }

    private setReturnToStart(bool: boolean): void {
        this.returnToStart = bool;
    }

    private refreshReturnToStartState(): void {
        if (!this.routeRules?.RouteMatchingRules || !this.visits?.length) {
            return;
        }

        const currentlyRequiresStartLocation: boolean = this.routeRules.RouteMatchingRules.StartRouteOnDepartStartingLocation;
        const currentlyRequiresEndLocation: boolean = this.routeRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation;

        if (!currentlyRequiresStartLocation || !currentlyRequiresEndLocation) {
            this.setReturnToStart(false);
            return;
        }

        let startLocation: google.maps.LatLngLiteral;
        let endLocation: google.maps.LatLngLiteral;

        if (!this.visits[0]?.RouteStopLocation) {
            return;
        }

        startLocation = {
            lat: this.visits[0].RouteStopLocation.Latitude,
            lng: this.visits[0].RouteStopLocation.Longitude
        }

        if (this.visits[this.visits.length - 1]?.RouteStopLocation) {
            endLocation = {
                lat: this.visits[this.visits.length - 1].RouteStopLocation.Latitude,
                lng: this.visits[this.visits.length - 1].RouteStopLocation.Longitude
            };
        }

        if (!endLocation) {
            const startVisit = this.visits[0];
            const endVisit = this.visits[this.visits.length - 1];

            if (!startVisit || !endVisit) {
                return;
            }

            this.pasteVisitLocationIntoVisit(startVisit, endVisit);
            this.setReturnToStart(true);
            this.validate();

            if (!this.validation.valid) {
                this.showRecalculateWarning();
            }

            return;

        } else if (startLocation.lat === endLocation.lat && startLocation.lng === endLocation.lng) {
            this.setReturnToStart(true);
        }
    }

    private setReturnToStartLocation(): void {
        if (!this.returnToStart || !this.visits?.length) {
            return;
        }

        const startVisit = this.visits[0];
        const endVisit = this.visits[this.visits.length - 1];

        if (!startVisit || !endVisit) {
            return;
        }

        if (!this.routeRules.RouteMatchingRules.EndRouteOnArriveAtEndingLocation) {
            if (this.onReturnToStart) {
                this.onReturnToStart();
            }

            return;
        }

        this.pasteVisitLocationIntoVisit(startVisit, endVisit);
        this.calculateRoute();
    }

    private pasteVisitLocationIntoVisit(from: IPlannedRouteVisit, to: IPlannedRouteVisit): void {

        to.Name = from.Name;
        to.GeofenceId = from.GeofenceId;
        to.Geofence = from.Geofence;
        to.RouteStopLocation = copy(from.RouteStopLocation);
        to.RouteStopLocationJson = JSON.stringify(to.RouteStopLocation);

        this.schedulingService.setVisitAsDirty(to);
        this.emitVisits();
    }
}

export class ScheduleVisitsListComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            visits: "<?",
            routeRules: "<?",
            startMoment: "<",
            forceVisitsUpdate: "<?",
            onChanged: "&",
            onReturnToStart: "&",
            onCalculatingRoute: "&",
            onStartMomentChanged: "&"
        };

        this.controller = ScheduleVisitsListComponentController;
        this.template = require("./scheduleVisitsList.component.html");
    }
}
